import { Box, Button, LinearProgress, Typography } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import EnrollmentCount from 'pages/Scheduling/EnrollmentCount'
import TeacherCell from 'pages/Scheduling/TeacherCell'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink } from 'react-router-dom'
import { search } from 'store/scheduling/course-session'
import { ROWS_PER_PAGE_OPTIONS } from 'constants'
import _ from 'lodash'
import NoRecords from 'components/NoRecords'

const basePath = '/scheduling/course-sessions'

export default function SchedulingCourseSessionsList({ list }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const academicYear = useSelector(state => state.session?.academicYear)
  const academicYearMarkingPeriods = academicYear?.markingPeriods
  const { query, count, loading } = useSelector(state => state.scheduling.courseSession)

  const [paginationModel, setPaginationModel] = useState({
    page: query?.limit ? Math.floor((query?.offset || 0) / query.limit) : 0,
    pageSize: query?.limit || 10,
  })

  const getTerm = mp => {
    if (academicYearMarkingPeriods?.length === 1 && mp?.length === 1) return 'Summer'
    if (mp?.length === academicYearMarkingPeriods?.length) return 'Full Year'
    return mp?.map(item => item.name).join(', ')
  }

  const getEnrollmentCell = item => (
    <EnrollmentCount enrolled={item?.enrollmentCount} quota={item?.quota} />
  )

  const columns = [
    {
      field: 'course',
      headerName: t('Course Name'),
      flex: 1.5,
      renderCell: ({ row }) => (
        <NavLink to={`${basePath}/${row.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
          <Typography variant="body2" sx={{ '&:hover': { textDecoration: 'underline' } }}>
            {row.course?.name}
          </Typography>
        </NavLink>
      ),
    },
    { field: 'name', headerName: t('Session Name'), flex: 1 },
    {
      field: 'teacher',
      headerName: t('Teacher'),
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => <TeacherCell enrollments={row.courseSessionEnrollments} />,
    },
    {
      field: 'term',
      headerName: t('Term'),
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => getTerm(row.markingPeriods),
    },
    {
      field: 'enrollment',
      headerName: t('Enrollment'),
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => getEnrollmentCell(row),
    },
    { field: 'periodCount', headerName: t('Periods'), flex: 1 },
    {
      field: 'actions',
      headerName: t('Action'),
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Button variant="outlined" component={NavLink} to={`${basePath}/${row.id}`} color="primary" size="small">
          {t('More')}
        </Button>
      ),
    },
  ]

  const handlePaginationChange = model => {
    setPaginationModel(model)
    dispatch(search({ ...query, limit: model.pageSize, offset: model.page * model.pageSize }))
  }

  if (loading) return <LinearProgress sx={{ width: '100%' }} />

  return (
    <Box>
      <DataGrid
        rows={list ?? []}
        columns={columns}
        getRowId={row => row.id}
        rowCount={count ?? 0}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        pageSizeOptions={_.uniq([query?.limit || 10, ...ROWS_PER_PAGE_OPTIONS])}
        autoHeight
        disableColumnMenu
        disableRowSelectionOnClick
        loading={!list}
        slots={{ noRowsOverlay: NoRecords }}
        sx={{ '& .MuiDataGrid-cell': { alignItems: 'center', display: 'flex' } }}
      />
    </Box>
  )
}
